import type {IAugmentedJQuery, IOnDestroy, IPostLink, IWindowService} from 'angular';
import type {BarcodeScannerService} from '../barcode-scanner.service';
import type {Subscription} from 'rxjs';
import type {Component} from 'typings/portaro.fe.types';

class KpBarcodeScannerInputControlComponentController implements KpBarcodeScannerInputControlComponentBindings, IPostLink, IOnDestroy {

    public allowedAutomaticStart: boolean; // component binding

    private readonly LOCAL_STORAGE_KEY = 'cameraScannerAllowed';

    // class attr. selectors:
    private readonly INPUT_GROUP_CLASS: string = 'input-group';
    private readonly INPUT_GROUP_HIDDEN_CLASS: string = 'input-group-hidden';

    // DOM elements:
    private readonly element: HTMLElement; // native self reference
    private inputElement: HTMLInputElement;
    private inputGroupElement: Element;

    private subscription: Subscription;
    private isScannerEnabled: boolean;

    /*@ngInject*/
    constructor(private barcodeScannerService: BarcodeScannerService,
                private $window: IWindowService,
                $element: IAugmentedJQuery) {
        this.element = $element[0];
    }

    $postLink(): void {

        this.inputGroupElement = this.element.closest(`.${this.INPUT_GROUP_HIDDEN_CLASS}`)
        this.inputElement = this.inputGroupElement.querySelector('input[kp-barcode-scanner]');

        if (this.barcodeScannerService.isUsable()) {
            this.showControls();
        }

        this.subscription =
            this.barcodeScannerService.getScannerEnabledState$()
                .subscribe((state) => this.setState(state));

        if (this.allowedAutomaticStart && !this.isScannerEnabled) {
            const enableScanner = this.loadEnableValue();
            if (enableScanner) {
                this.barcodeScannerService.startScanning();
            }
        }
    }

    $onDestroy(): void {
        this.subscription.unsubscribe();
    }

    public toggle(): void {

        this.unFocusInput(); // to unsubscribe before state switch
        this.barcodeScannerService.toggleScanning();
        this.focusInput(); // to (re)subscribe after switch
        if (this.allowedAutomaticStart) {
            this.storeEnabledValue(this.isScannerEnabled);
        }
    }

    public isToggled(): boolean {
        return this.isScannerEnabled;
    }

    private focusInput(): void {
        this.inputElement.focus();
    }

    private unFocusInput(): void {
        this.inputElement.blur();
    }

    private setState(isScannerEnabled: boolean) {
        this.isScannerEnabled = isScannerEnabled;
        if (isScannerEnabled) {
            this.enableInputScannerMode();
        } else {
            this.disableInputScannerMode();
        }
    }

    private enableInputScannerMode() {
        this.inputElement.readOnly = true;
    }

    private disableInputScannerMode() {
        this.inputElement.readOnly = false;
    }

    private showControls() {
        this.inputGroupElement.classList.add(this.INPUT_GROUP_CLASS);
        this.inputGroupElement.classList.remove(this.INPUT_GROUP_HIDDEN_CLASS);
    }

    private loadEnableValue(): boolean {
        return this.$window.localStorage.getItem(this.LOCAL_STORAGE_KEY) === 'true';
    }

    private storeEnabledValue(value: boolean): void {
        this.$window.localStorage.setItem(this.LOCAL_STORAGE_KEY, String(value));
    }
}

interface KpBarcodeScannerInputControlComponentBindings {
    allowedAutomaticStart: boolean
}

export class KpBarcodeScannerInputControlComponent implements Component<KpBarcodeScannerInputControlComponentBindings> {

    public static readonly componentName = 'kpBarcodeScannerInputControl';
    public templateUrl = require('./kp-barcode-scanner-input-control.tpl.ftl');
    public controller = KpBarcodeScannerInputControlComponentController;

    public bindings = {
        allowedAutomaticStart: '<'
    } as const;

}